import { DrawerBase } from '../../core/abstraction/DrawerBase';
import { GAME_MODES, GAME_SCENE } from '../../core/constats';
import { gameConfig } from '../../core/configs';
import { preBonusResponseHelper } from '../../services/helperService/PreBonusResponseHelper';
import { spinButtonHelper } from '../../services/helperService/SpinButtonHelper';
import { delay } from '../../core/helpers/delay';
import { popupHelper } from '../../services/helperService/PopupHelper';
import { soundService } from '../../services/helperService/PixiSoundService';
import { BONUS_RESPONSE_TOTAL_AMOUNT, SCREEN_TRANSACTION_STATE } from '../../stateManagment/gameState';
import { gamePadHelper } from '../../services/helperService/GamePadHelper';
import { loadingStageService } from '../../shared/loading/LoadingStageService';
import { crystalPerTypeHelper } from '../../services/helperService/CrystalPerTypeHelper';
import { userService } from '../../services/helperService/UserService';
import { spinResponseHelper } from '../../services/helperService/SpinResponseHelper';
import { buyBonusButtonHelper } from '../../services/helperService/BuyBonusButtonHelper';
import { autoSpinHelper } from '../../services/helperService/AutoSpinHelper';
import {playerService} from "../../services/PlayerService";
import {gameService} from "../../services/GameService";

export class BaseGameDrawer extends DrawerBase {
  constructor(drawers) {
    super();
    this.parent = null;
    this.once = new Set();
    this.mainScene = this.createContainer('mainScene');
    this.preBonusScene = this.createContainer('preBonusScene');
    this.bonusScene = this.createContainer('bonusScene');
    this.screenChangeContainer = this.createContainer('screenChangeContainer');
    this.drawers = drawers;
    this.mainContainers = {};
    this.preBonusSceneContainers = {};
    this.bonusSceneContainers = {};
    this._setupScreenChangeAnimationContainer(this.screenChangeContainer);
    this.screenChangeDuration = 2750;
  }

  destroyMainScene() {
    this.mainScene.visible = false;
  }

  destroyPreBonusScene() {
    this.preBonusScene.visible = false;
  }

  destroyBonusScene() {
    this.bonusScene.visible = false;
  }

  startScreenChangeAnimation({ prev, next }) {
    return new Promise((resolve) => {
      if (prev) {
        if (prev === GAME_SCENE.MAIN && next === GAME_SCENE.PRE_BONUS) {
          this.mainToPreBonusStart();
          delay(() => {
            this.mainToPreBonusEnd();
            resolve();
          }, this.screenChangeDuration);
        } else if (prev === GAME_SCENE.PRE_BONUS && next === GAME_SCENE.BONUS) {
          this.preBonusToBonusStart();
          delay(() => {
            this.preBonusToBonusEnd();
            resolve();
          }, this.screenChangeDuration);
        } else if (prev === GAME_SCENE.BONUS && next === GAME_SCENE.MAIN) {
          this.bonusToMainStart();
          delay(() => {
            this.bonusToMainEnd();
            resolve();
          }, this.screenChangeDuration);
        }
      } else {
        if(next === GAME_SCENE.PRE_BONUS) {
          this.addSubscriber(loadingStageService.stage.subscribe(val => {
            if (val === loadingStageService.max) {
              delay(() => {
                this.drawers.gamePad.application.handlePreBonusSpinButtonClick()()
              }, 1000)
            }
          }, true));
        }

        if (next === GAME_SCENE.BONUS) {
          this.addSubscriber(loadingStageService.stage.subscribe(val => {
            if (val === loadingStageService.max) {
              delay(() => {
                this.drawers.bonusPad.setWheelCrystals();
              }, 2000);
            }
          }, true));
        }
      }
    });
  }

  _setupScreenChangeAnimationContainer(content) {
    content.visible = false;
    const rect = this.createGraphics()
      .beginFill(0x000000)
      .drawRect(
        0,
        0,
        gameConfig.width,
        gameConfig.height,
      )
      .endFill();
    this.setupButtonMode(rect);
    this.registerClick(rect, event => {
      event.stopPropagation();
    });
    rect.alpha = 0;
    content.addChild(rect);
  }

  _defineContainers(containers, getter, mode = GAME_MODES.MOBILE) {
    containers.forEach(({ drawer, containerName }) => {
      this.drawers[drawer].getApplication(mode).defineContainer(getter(containerName));
    });
  }

  _setupSceneContainer({
    containerNames,
    thisTarget,
    parent,
  }) {
    const { containers, list } = containerNames.reduce((acc, name) => {
      const container = this.createContainer(name);
      acc.containers[name] = container;
      acc.list.push(container);
      return acc;
    }, {
      containers: {},
      list: [],
    });
    this[thisTarget] = containers;
    parent.addChild(...list);
  }

  getMainSceneContainer(name) {
    return this.mainContainers[name] || null;
  }

  getBonusSceneContainer(name) {
    return this.bonusSceneContainers[name] || null;
  }

  getPreBonusSceneContainer(name) {
    return this.preBonusSceneContainers[name] || null;
  }

  defineParent(parent) {
    this.parent = parent;
  }

  /**
   * @desc abstraction
   * @param mainScene
   */
  setupMainSceneContainer(mainScene) {
    throw new Error(`unimplemented setupMainSceneContainer in ${this.constructor.name}`);
  }

  /**
   * @desc abstraction
   */
  defineMainSceneContainer() {
    throw new Error(`unimplemented defineMainSceneContainer in ${this.constructor.name}`);
  }

  /**
   * @desc abstraction
   */
  setupPreBonusSceneContainer() {
    throw new Error(`unimplemented setupPreBonusSceneContainer in ${this.constructor.name}`);
  }

  /**
   * @desc abstraction
   */
  definePreBonusSceneContainer() {
    throw new Error(`unimplemented definePreBonusSceneContainer in ${this.constructor.name}`);
  }

  setupBonusSceneContainer() {
    throw new Error(`unimplemented setupBonusSceneContainer in ${this.constructor.name}`);
  }

  defineBonusSceneContainer() {
    throw new Error(`unimplemented defineBonusSceneContainer in ${this.constructor.name}`);
  }

  executeBaseMainScene() {
    crystalPerTypeHelper.reset();
    this.mainScene.children = [];
    this.setupMainSceneContainer(this.mainScene);
    this.defineMainSceneContainer();
  }

  executeBasePreBonusScene() {
    this.preBonusScene.children = [];
    this.setupPreBonusSceneContainer(this.preBonusScene);
    this.definePreBonusSceneContainer();
  }

  executeBaseBonusScene() {
    crystalPerTypeHelper.reset();
    spinButtonHelper.disable();
    gamePadHelper.enableAmountChangeState();
    gamePadHelper.enableSpinCountChangeState();
    spinResponseHelper.resetSpinStarted();
    this.bonusScene.children = [];
    this.setupBonusSceneContainer(this.bonusScene);
    this.defineBonusSceneContainer();
  }

  mainToPreBonusStart() {
    SCREEN_TRANSACTION_STATE.next(true);
    this.screenChangeContainer.visible = true;
    this.destroyMainSceneObservers();
    preBonusResponseHelper.resetReSpinCount();
    preBonusResponseHelper.resetMultiplayer();
    preBonusResponseHelper.resetFreeSpin();
    preBonusResponseHelper.resetPreBonusResponseAnimateState();
    spinButtonHelper.disable();
    this.preBonusScene.visible = false;
    this.drawers.wheel.screenTransaction();
  }

  mainToPreBonusEnd() {
    SCREEN_TRANSACTION_STATE.next(false);
    this.screenChangeContainer.visible = false;
    popupHelper.reset();
    spinButtonHelper.enable();
    this.destroyMainScene();
    this.destroyBonusScene();
    this.preBonusScene.visible = true;
    soundService.playPreBonusSceneBackground();
    delay(() => {
      this.drawers.gamePad.application.handlePreBonusSpinButtonClick()()
    }, 1000)
  }

  preBonusToBonusStart() {
    SCREEN_TRANSACTION_STATE.next(true);
    BONUS_RESPONSE_TOTAL_AMOUNT.next(0);
    this.screenChangeContainer.visible = true;
    this.bonusScene.visible = false;
    this.destroyPreBonusSceneObservers();
    spinButtonHelper.disable();
    this.drawers.preBonus.screenTransaction();
  }

  preBonusToBonusEnd() {
    SCREEN_TRANSACTION_STATE.next(false);
    popupHelper.reset();
    this.screenChangeContainer.visible = false;
    this.destroyMainScene();
    this.destroyPreBonusScene();
    this.bonusScene.visible = true;
    soundService.playBonusSceneBackground();
    delay(() => {
      this.drawers.bonusPad.setWheelCrystals();
    }, 1000);
  }

  bonusToMainStart() {
    SCREEN_TRANSACTION_STATE.next(true);
    buyBonusButtonHelper.enable();
    gamePadHelper.enableAmountChangeState();
    gamePadHelper.enableSpinCountChangeState();
    gamePadHelper.enableAutoSpin();
    autoSpinHelper.reset();
    this.destroyBonusSceneObservers();
    this.screenChangeContainer.visible = true;
    spinButtonHelper.disable();
    this.mainScene.visible = false;
    this.drawers.bonusWheel.screenTransaction();
  }

  bonusToMainEnd() {
    SCREEN_TRANSACTION_STATE.next(false);
    popupHelper.reset();
    userService.resetBonusLastResult();
    this.screenChangeContainer.visible = false;
    this.destroyBonusScene();
    this.destroyPreBonusScene();
    this.mainScene.visible = true;
    spinButtonHelper.enable();
    soundService.playMainSceneBackground();
    if (gameService.isRealMode) {
      playerService.getBalance();
    }
  }

  async execute({ scene, parent, prevScene }) {
    await this.loadResource();
    this._resetObserverOpenedState();
    this.parent.addChild(
      this.mainScene,
      this.preBonusScene,
      this.bonusScene,
      this.screenChangeContainer,
    );
    this.startScreenChangeAnimation({ prev: prevScene, next: scene });
    switch (scene) {
      case GAME_SCENE.MAIN:
        return this.executeMainScene({ scene, parent });
      case GAME_SCENE.PRE_BONUS:
        return this.executePreBonusScene({ scene, parent });
      case GAME_SCENE.BONUS:
        return this.executeBonusScene({ scene, parent });
      default:
        return null;
    }
  }
}
